import React, { useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Briefcase, Building2, CheckCircle2, Users, GraduationCap, Award, Bell, Hourglass, Compass,
} from 'lucide-react';
import { Metric, Spinner, Empty } from './shared';

const METRIC_DEFS = [
  ['programs', 'Open programs', Briefcase],
  ['companies', 'Participating companies', Building2],
  ['approved_opportunities', 'Approved opportunities', CheckCircle2],
  ['applicants', 'Student applicants', Users],
  ['placed', 'Students placed', GraduationCap],
  ['completed', 'Internships completed', Award],
];

/** School-wide internship overview: metrics, what needs attention, live programmes and careers. */
export default function InternshipOverview({ data, loading, onOpenProgram, onOpenPrograms }) {
  const [careerProgram, setCareerProgram] = useState('all');
  const [allCareers, setAllCareers] = useState(false);

  const careers = useMemo(() => {
    if (!data) return [];
    if (careerProgram === 'all') return data.careers || [];
    return (data.programs.find(p => p.id === careerProgram)?.careers) || [];
  }, [data, careerProgram]);

  if (loading) return <Spinner />;
  if (!data) return <Empty testId="overview-error" text="Could not load your internship dashboard. Please refresh." />;

  const mine = data.needs_attention.filter(x => x.group === 'your_action');
  const waiting = data.needs_attention.filter(x => x.group === 'waiting');
  const shownCareers = allCareers ? careers : careers.slice(0, 5);

  return (
    <div data-testid="internships-overview">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2.5 mb-6" data-testid="overview-metrics">
        {METRIC_DEFS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => onOpenPrograms(key)} disabled={!data.metrics[key]}
            className="h-full text-left disabled:cursor-default" data-testid={`metric-link-${key}`}>
            <Metric label={label} value={data.metrics[key]} icon={Icon}
              tone={key === 'completed' ? 'accent' : 'default'} testId={`metric-${key}`} />
          </button>
        ))}
      </div>

      <AttentionGroup title="Needs your action" icon={Bell} items={mine} onOpenProgram={onOpenProgram}
        testId="needs-your-action" emptyText="Nothing waiting on you right now." />
      <AttentionGroup title="Waiting on others" icon={Hourglass} items={waiting} onOpenProgram={onOpenProgram}
        testId="waiting-on-others" emptyText={null} muted />

      <section>
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-1.5">
            <Compass className="h-3.5 w-3.5 text-[#F97D6A]" />Internships by career
          </h3>
          <Select value={careerProgram} onValueChange={setCareerProgram}>
            <SelectTrigger className="h-9 w-[230px] rounded-lg border-gray-200 text-xs bg-white" data-testid="careers-program-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All programs</SelectItem>
              {data.programs.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {shownCareers.length === 0 ? (
          <p className="bg-white rounded-2xl border border-[#EFF0F2] px-4 py-5 text-sm text-[#9CA3AF] text-center" data-testid="careers-empty">
            No approved internships are mapped to a career yet.
          </p>
        ) : (
          <div className="bg-white rounded-2xl border border-[#EFF0F2] overflow-hidden" data-testid="careers-table">
            <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[460px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#F3F4F6]">
                  <th className="px-4 py-2.5 font-semibold">Career</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Approved opportunities</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Students placed</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Completed</th>
                </tr>
              </thead>
              <tbody>
                {shownCareers.map(c => (
                  <tr key={c.slug} className="border-b border-[#F7F8F9] last:border-0 hover:bg-[#FAFBFC] cursor-pointer"
                    onClick={() => onOpenProgram(
                      careerProgram === 'all'
                        ? (data.programs.find(p => (p.careers || []).some(x => x.slug === c.slug))?.id)
                        : careerProgram,
                      c.placed ? 'placements' : 'companies', `q:${c.name}`)}
                    data-testid={`career-row-${c.slug}`}>
                    <td className="px-4 py-2.5 text-sm text-[#1F2937] font-medium">{c.name}</td>
                    <td className="px-3 py-2.5 text-sm text-[#4B5563] text-right">{c.approved_opportunities}</td>
                    <td className="px-3 py-2.5 text-sm text-[#4B5563] text-right">{c.placed}</td>
                    <td className="px-3 py-2.5 text-sm text-[#4B5563] text-right">{c.completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {careers.length > 5 && (
              <button onClick={() => setAllCareers(v => !v)} className="w-full py-2 text-[11px] font-semibold text-[#F06651] hover:bg-[#FFF6F4] transition-colors" data-testid="careers-view-all">
                {allCareers ? 'Show fewer' : `View all ${careers.length} careers`}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function AttentionGroup({ title, icon: Icon, items, onOpenProgram, testId, emptyText, muted }) {
  if (!items.length && !emptyText) return null;
  return (
    <section className="mb-6">
      <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-2.5 flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${muted ? 'text-[#9CA3AF]' : 'text-[#F97D6A]'}`} />{title}
      </h3>
      {items.length === 0 ? (
        <p className="bg-white rounded-2xl border border-[#EFF0F2] px-4 py-5 text-sm text-[#9CA3AF] text-center" data-testid={`${testId}-empty`}>
          {emptyText}
        </p>
      ) : (
        <div className="space-y-2" data-testid={testId}>
          {items.map(item => (
            <div key={item.kind} className="bg-white rounded-2xl border border-[#EFF0F2] px-4 py-3" data-testid={`attention-${item.kind}`}>
              <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-2">
                <span className={`inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-[11px] font-bold ${muted ? 'bg-[#F5F6F8] text-[#6B7280]' : 'bg-[#FFF0ED] text-[#F06651]'}`}>{item.count}</span>
                {item.label}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {item.programs.map(p => (
                  <button key={p.program_id} onClick={() => onOpenProgram(p.program_id, item.tab, item.kind)}
                    className="text-[11px] font-medium text-[#4B5563] bg-[#F5F6F8] hover:bg-[#FFF0ED] hover:text-[#F06651] rounded-lg px-2.5 py-1 transition-colors"
                    data-testid={`attention-link-${item.kind}-${p.program_id}`}>
                    {p.program_name} · {p.count}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
